<template>
  <v-container id="orders-settings" fluid>
    <v-card outlined>
      <validation-observer ref="form">
        <v-card-text>
          <v-tabs v-model="activeTab" grow>
            <v-tab>{{ $t('order.defaultDeadlines') }}</v-tab>
            <v-tab>{{ $t('settings.preparation') }}</v-tab>
            <v-tab>{{ $t('order.pickingSlip') }}</v-tab>
            <v-tab>{{ $t('settings.displaySettings') }}</v-tab>
            <v-tab>{{ $t('settings.notifications') }}</v-tab>
            <v-tab>{{ $t('settings.orchestration') }}</v-tab>
            <v-tab>{{ $t('settings.paymentMethod.tabLabel') }}</v-tab>
            <v-tab>{{ $t('settings.permissions.tab') }}</v-tab>
            <v-tab-item eager>
              <delays-settings
                ref="delaysSettings"
                :isDark="isDark"
                :loadedSettings="loadedSettings"
                :loading="loading"
              />
            </v-tab-item>
            <v-tab-item eager>
              <preparation-settings ref="preparationSettings" :loadedSettings="loadedSettings" :loading="loading" />
            </v-tab-item>
            <v-tab-item eager>
              <slip-delivery :loadedSettings="loadedSettings" :loading="loading" />
            </v-tab-item>
            <v-tab-item eager>
              <display-settings ref="displaySettings" :loadedSettings="loadedSettings" :loading="loading" />
            </v-tab-item>
            <v-tab-item eager>
              <notification-settings :loaded-settings="loadedSettings" :loading="loading" />
            </v-tab-item>
            <v-tab-item eager>
              <orchestration-settings
                ref="orchestrationSettings"
                :loadedSettings="loadedSettings"
                :loading="loading"
              ></orchestration-settings>
            </v-tab-item>
            <v-tab-item eager>
              <payment-method-settings :loaded-settings="loadedSettings" :loading="loading" />
            </v-tab-item>
            <v-tab-item eager>
              <permissions-settings :loaded-settings="loadedSettings" />
            </v-tab-item>
          </v-tabs>
          <v-row justify="center">
            <v-btn :loading="loading" class="ma-2 text-uppercase" color="primary" outlined @click="validate">
              <span v-if="areNewSettings">{{ $t('settings.save') }}</span>
              <span v-else>{{ $t('settings.update') }}</span>
            </v-btn>
          </v-row>
        </v-card-text>
      </validation-observer>
    </v-card>
  </v-container>
</template>

<script>
  import { EventBus } from '../../event-bus'
  import { mapState } from 'vuex'
  import DelaysSettings from '../../components/settings/DelaysSettings'
  import SlipDeliverySettings from '@/components/settings/SlipDeliverySettings'
  import DisplaySettings from '../../components/settings/DisplaySettings'
  import OrchestrationSettings from '../../components/settings/OrchestrationSettings'
  import NotificationSettings from '../../components/settings/NotificationSettings'
  import PreparationSettings from '../../components/settings/PreparationSettings.vue'
  import PaymentMethodSettings from '../../components/settings/PaymentMethodSettings.vue'
  import PermissionsSettings from '../../components/settings/PermissionsSettings.vue'
  import { initialEbookingCancelRoles } from '@/helpers/pickupCancelPermission'
  import { ValidationObserver } from 'vee-validate'
  import { convertBoxFromApiFormat, convertBoxToApiFormat } from '@/helpers/helper'
  import {
    DEFAULT_ORDERS_VISIBILITY_CONDITIONS,
    cloneConditions,
    validateOrdersVisibilityConditions,
  } from '@/helpers/orderVisibility'

  export default {
    name: 'OrdersSettings',
    components: {
      delaysSettings: DelaysSettings,
      slipDelivery: SlipDeliverySettings,
      displaySettings: DisplaySettings,
      orchestrationSettings: OrchestrationSettings,
      notificationSettings: NotificationSettings,
      preparationSettings: PreparationSettings,
      paymentMethodSettings: PaymentMethodSettings,
      permissionsSettings: PermissionsSettings,
      validationObserver: ValidationObserver,
    },
    data() {
      return {
        selectedCountry: undefined,
        areNewSettings: false,
        loading: false,
        countries: [],
        loadedSettings: {},
        settingsExist: false,
        defaultRoles: ['oms-admin', 'oms-customer-service', 'oms-shop-manager', 'default-user'],
        activeTab: 0,
        // Index of the "Display settings" tab that hosts the orders-visibility block.
        displaySettingsTabIndex: 3,
      }
    },
    computed: {
      ...mapState(['currentStore', 'isCustomerService', 'isAdmin', 'isDark', 'isOctipasAdmin']),
      settingExist() {
        return Object.keys(this.loadedSettings).length > 0
      },
    },
    mounted() {
      if (this.$route.params.id) {
        this.selectedCountry = this.$route.params.id
        this.getSettingsByCountry()
      }
    },
    methods: {
      applyCancelPermissions(res) {
        const pickupRoles = Array.isArray(res.permissions?.pickup_order_cancel_roles)
          ? res.permissions.pickup_order_cancel_roles
          : []
        this.$set(this.loadedSettings, 'pickupOrderCancelRoles', pickupRoles.slice())
        this.$set(this.loadedSettings, 'ebookingCancelRoles', initialEbookingCancelRoles(res).slice())
      },
      getSettingsByCountry() {
        // get the settings of the country selected
        this.loading = true
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        this.$Amplify.API.get('settings', '/v1/settings/' + this.selectedCountry, myInit)
          .then((res) => {
            this.applyLoadedSettings(res)
            this.loading = false
          })
          .catch(async (e) => {
            console.log(e)
            if (e.response && e.response.status == 404) {
              // New country: prefill the whole form from the back default (single source of
              // truth) via the same mapping as a real settings response — no hardcoded defaults.
              this.areNewSettings = true
              try {
                const defaultRes = await this.$Amplify.API.get('settings', '/v1/settings/default', myInit)
                this.applyLoadedSettings(defaultRes)
                // The sentinel carries an in-memory _id; a brand-new country is not persisted yet.
                this.loadedSettings.id = undefined
                EventBus.$emit('displaySnackbar', {
                  display: true,
                  text: this.$t('settings.noCountry'),
                  color: 'warning',
                })
              } catch (defaultError) {
                // Defaults are back-owned (no client fallback): fail cleanly with a single clear
                // message rather than leaving a half-empty, unsavable form.
                console.error('[OrdersSettings] default settings fetch failed', this.selectedCountry, defaultError)
                EventBus.$emit('displaySnackbar', {
                  display: true,
                  text: this.$t('settings.defaultSettingsLoadError'),
                  color: 'error',
                })
              }
            } else {
              EventBus.$emit('displaySnackbar', {
                display: true,
                text: e.message,
                color: 'error',
              })
            }
            this.loading = false
          })
      },
      applyLoadedSettings(res) {
        if (res.delays) {
          //------------------------------------------- global section -------------------------------------------//

          this.loadedSettings.globalClientDelay = res.delays.global.maximum_pickup_time / 86400
          let globalPickupTimeout = res.delays.global.pickup_timeout
          this.$set(this.loadedSettings, 'activatePickupTimeout', globalPickupTimeout.activate)
          this.$set(
            this.loadedSettings,
            'activatePickupDeadlineAdjustment',
            globalPickupTimeout.activate_pickup_deadline_adjustment
          )
          this.loadedSettings.pickupDeadlineAdjustmentHour = globalPickupTimeout.pickup_deadline_adjustment_hour

          this.$set(
            this.loadedSettings,
            'activatePickupDeadlineExtension',
            globalPickupTimeout.activate_pickup_deadline_extension
          )
          this.$set(
            this.loadedSettings,
            'pickupDeadlineExtensionLimit',
            globalPickupTimeout.pickup_deadline_extension_limit / 86400
          )

          this.loadedSettings.clickAndCollectSellerDelay = res.delays.click_and_collect.maximum_response_time / 3600
          this.loadedSettings.clickAndCollectPreparationSlotsSearchPeriod =
            res.delays.click_and_collect.preparation_slots_search_period / 86400

          //---------------------------------------- e-reservation section ----------------------------------------//

          this.loadedSettings.eReservationSellerDelay = res.delays.click_and_reserve.maximum_response_time / 3600 // hour
          this.loadedSettings.eReservationClientDelay = res.delays.click_and_reserve.maximum_pickup_time / 86400
          this.loadedSettings.eReservationPreparationSlotsSearchPeriod =
            res.delays.click_and_reserve.preparation_slots_search_period / 86400

          let eReservationPickupTimeout = res.delays.click_and_reserve.pickup_timeout
          this.$set(this.loadedSettings, 'activateClickAndReservePickupTimeout', eReservationPickupTimeout.activate)
          this.$set(
            this.loadedSettings,
            'activateClickAndReservePickupDeadlineAdjustment',
            eReservationPickupTimeout.activate_pickup_deadline_adjustment
          )
          this.loadedSettings.clickAndReservePickupDeadlineAdjustmentHour =
            eReservationPickupTimeout.pickup_deadline_adjustment_hour
          this.$set(
            this.loadedSettings,
            'activateClickAndReservePickupDeadlineExtension',
            eReservationPickupTimeout.activate_pickup_deadline_extension
          )
          this.$set(
            this.loadedSettings,
            'clickAndReservePickupDeadlineExtensionLimit',
            eReservationPickupTimeout.pickup_deadline_extension_limit / 86400
          )

          //--------------------------------------- ship from store section ---------------------------------------//

          this.loadedSettings.shipFromStoreSellerDelay = res.delays.ship_from_store.maximum_response_time / 3600 // hour
          this.loadedSettings.shipFromStorePreparationSlotsSearchPeriod =
            res.delays.ship_from_store.preparation_slots_search_period / 86400

          this.loadedSettings.id = res._id
        }
        this.loadedSettings.logo_header_url = res.delivery_slip ? res.delivery_slip.logo_header_url : undefined
        this.loadedSettings.footer_text = res.delivery_slip ? res.delivery_slip.footer_text : undefined
        if (res.delivery_slip) {
          this.loadedSettings.display_fields = {
            email: res.delivery_slip.display_fields.email,
            phone: res.delivery_slip.display_fields.phone,
            barcode: res.delivery_slip.display_fields.barcode,
          }
        } else {
          this.loadedSettings.display_fields = {
            email: true,
            phone: true,
            barcode: false,
          }
        }
        if (res.display) {
          this.loadedSettings.displayParcelDeliveryButton = res.display.display_parcel_delivery_button
          this.loadedSettings.displayEbookingDeliveryButton = res.display.display_ebooking_delivery_button
          this.loadedSettings.displayDeliverPackageToTransporterButton =
            res.display.display_deliver_package_to_transporter_button
          this.loadedSettings.displayFastPreparationButton = res.display.display_fast_preparation_button
          this.loadedSettings.displayStockIndicatorOnProduct = res.display.display_stock_indicator_on_product
          this.loadedSettings.displayCustomCancelReason = res.display.display_custom_cancel_reason
          this.loadedSettings.displayProductsCustomCancelReason = res.display.display_products_custom_cancel_reason
          this.loadedSettings.displayOrderToPutBackInStock = res.display.display_order_to_put_back_in_stock
          this.loadedSettings.displayAddressEditButtons = res.display.display_address_edit_buttons
          this.loadedSettings.displayOrdersGlobalSearch = res.display.display_orders_global_search
          this.loadedSettings.itemCancelReasons = res.display.item_cancel_reasons
          this.loadedSettings.orderCancelReasons = res.display.order_cancel_reasons
          this.loadedSettings.displayCustomerEditButtons = res.display.display_customer_edit_buttons
          this.loadedSettings.displayForceDelivered = res.display.display_force_delivered_button
          this.loadedSettings.displayRetryBlockedOrdersButton = res.display.display_retry_blocked_orders_button
          this.loadedSettings.displayPickupDeadlineExtensionButton =
            res.display.display_pickup_deadline_extension_button
          this.loadedSettings.displayEbookingPickupDeadlineExtensionButton =
            res.display.display_ebooking_pickup_deadline_extension_button
          this.loadedSettings.displayWaitingPaymentOrdersMenu = res.display.display_waiting_payment_orders_menu
        } else {
          this.loadedSettings.displayParcelDeliveryButton = true
          this.loadedSettings.displayEbookingDeliveryButton = true
          this.loadedSettings.displayDeliverPackageToTransporterButton = true
          this.loadedSettings.displayFastPreparationButton = true
          this.loadedSettings.displayStockIndicatorOnProduct = true
          this.loadedSettings.displayCustomCancelReason = true
          this.loadedSettings.displayProductsCustomCancelReason = true
          this.loadedSettings.displayOrderToPutBackInStock = false
          this.loadedSettings.displayAddressEditButtons = ['oms-admin', 'oms-customer-service']
          this.loadedSettings.displayCustomerEditButtons = ['oms-admin', 'oms-customer-service']
          this.loadedSettings.displayOrdersGlobalSearch = []
          this.loadedSettings.itemCancelReasons = ['not_available', 'defective', 'already_reserved']
          this.loadedSettings.orderCancelReasons = [
            'client_changed_his_mind',
            'deteriorated_products',
            'ordered_multiple_times',
            'products_not_available',
            'customer_did_not_collect_order',
          ]
          this.loadedSettings.displayForceDelivered = ['oms-admin', 'oms-customer-service']
          this.loadedSettings.displayRetryBlockedOrdersButton = ['oms-admin']
          this.loadedSettings.displayPickupDeadlineExtensionButton = []
          this.loadedSettings.displayEbookingPickupDeadlineExtensionButton = []
          this.loadedSettings.displayWaitingPaymentOrdersMenu = []
        }
        if (res.display?.customer_information) {
          this.loadedSettings.customerInformation = {
            customerName: res.display.customer_information.customer_name,
            customerEmail: res.display.customer_information.customer_email,
            addresses: res.display.customer_information.addresses,
            fullPhoneNumber: res.display.customer_information.full_phone_number,
            lastFourDigitPhoneNumber: res.display.customer_information.last_four_digit_phone_number,
          }
        } else {
          this.loadedSettings.customerInformation = {
            customerName: this.defaultRoles,
            customerEmail: this.defaultRoles,
            addresses: this.defaultRoles,
            fullPhoneNumber: this.defaultRoles,
            lastFourDigitPhoneNumber: this.defaultRoles,
          }
        }
        // orders visibility (store view) — the API returns the saved conditions (or
        // the default as fallback) plus a read-only default_conditions reference.
        // Use `.length` checks (not `||`) since an empty array is truthy in JS.
        const ordersVisibility = res.display?.orders_visibility
        const defaultConditions = ordersVisibility?.default_conditions?.length
          ? ordersVisibility.default_conditions
          : DEFAULT_ORDERS_VISIBILITY_CONDITIONS
        this.$set(
          this.loadedSettings,
          'ordersVisibilityConditions',
          cloneConditions(ordersVisibility?.conditions?.length ? ordersVisibility.conditions : defaultConditions)
        )
        this.$set(this.loadedSettings, 'ordersVisibilityDefaultConditions', cloneConditions(defaultConditions))
        this.$set(this.loadedSettings, 'ordersVisibilityErrorKey', '')
        this.applyCancelPermissions(res)
        if (res.orchestration) {
          if (res.orchestration.max_daily_allocations) {
            const maxDailyAllocations = res.orchestration.max_daily_allocations
            if (maxDailyAllocations.by_default) {
              this.$set(
                this.loadedSettings,
                'maxDailyAllocationsByDefaultActivate',
                maxDailyAllocations.by_default.activate
              )
              this.loadedSettings.maxDailyAllocationsByDefaultValue = maxDailyAllocations.by_default.value
            } else {
              this.$set(this.loadedSettings, 'maxDailyAllocationsByDefaultActivate', false)
              this.loadedSettings.maxDailyAllocationsByDefaultValue = undefined
            }
            if (Array.isArray(maxDailyAllocations.per_locations)) {
              this.$set(this.loadedSettings, 'maxDailyAllocationsPerLocations', maxDailyAllocations.per_locations)
            } else {
              this.loadedSettings.maxDailyAllocationsPerLocations = []
            }
          }
        } else {
          this.$set(this.loadedSettings, 'maxDailyAllocationsByDefaultActivate', false)
          this.loadedSettings.maxDailyAllocationsByDefaultValue = undefined
          this.loadedSettings.maxDailyAllocationsPerLocations = []
        }

        if (res.notifications) {
          const notificationObject = {
            activate: res.notifications?.on_create_sub_order?.email?.activate || false,
            location: {
              activate: res.notifications?.on_create_sub_order?.email?.location.activate || false,
            },
            location_managers: {
              activate: res.notifications?.on_create_sub_order?.email?.location_managers.activate || false,
              restrict_to_main:
                res.notifications?.on_create_sub_order?.email?.location_managers.restrict_to_main || false,
            },
          }

          this.$set(this.loadedSettings, 'onCreateSubOrderEmail', notificationObject)
          this.loadedSettings.onCreateSubOrderPushNotificationActivate =
            res.notifications.on_create_sub_order.push_notification.activate

          let alerts = []
          if (Array.isArray(res.notifications.alerts) && res.notifications.alerts.length) {
            alerts = res.notifications.alerts.map((alert) => {
              return {
                name: alert.name,
                activate: alert.activate || false,
                type: alert.type || 'email',
                onStatus: alert.on_status,
                emails: alert.emails,
                templateId: alert.template_id,
              }
            })
          }
          this.$set(this.loadedSettings, 'alerts', alerts)
        } else {
          this.$set(this.loadedSettings, 'onCreateSubOrderEmail', {
            activate: false,
            location: {
              activate: false,
            },
            location_managers: {
              activate: false,
              restrict_to_main: false,
            },
          })
          this.loadedSettings.onCreateSubOrderPushNotificationActivate = true

          this.$set(this.loadedSettings, 'alerts', [])
        }
        if (res.delivery) {
          this.$set(
            this.loadedSettings,
            'lockerForPreparation',
            res.delivery.locker?.for_preparation?.activate || false
          )
          this.loadedSettings.lockerForPreparationTypes = res.delivery.locker?.for_preparation?.order_types || [
            'click_and_reserve',
            'click_and_collect',
            'ship_from_store',
            'inter_store',
          ]
          this.$set(this.loadedSettings, 'lockerForReception', res.delivery.locker?.for_reception?.activate || false)
          this.loadedSettings.lockerForReceptionTypes = res.delivery.locker?.for_reception?.order_types || [
            'in_store_pickup',
            'inter_store',
          ]
          const pickupCodeActivated = res.delivery?.pickup_code?.activate ? res.delivery.pickup_code.activate : false
          this.$set(this.loadedSettings, 'pickupCodeActivate', pickupCodeActivated)
          const pickupCodeDisplayAllowedRoles = res.delivery?.pickup_code?.display_allowed_roles
            ? res.delivery.pickup_code.display_allowed_roles
            : []
          this.$set(this.loadedSettings, 'pickupCodeDisplayAllowedRoles', pickupCodeDisplayAllowedRoles)
          const pickupCodeResendAllowedRoles = res.delivery?.pickup_code?.resend_allowed_roles
            ? res.delivery.pickup_code.resend_allowed_roles
            : []
          this.$set(this.loadedSettings, 'pickupCodeResendAllowedRoles', pickupCodeResendAllowedRoles)
        } else {
          this.$set(this.loadedSettings, 'lockerForPreparation', false)
          this.loadedSettings.lockerForPreparationTypes = [
            'click_and_reserve',
            'click_and_collect',
            'ship_from_store',
            'inter_store',
          ]
          this.$set(this.loadedSettings, 'lockerForReception', false)
          this.loadedSettings.lockerForReceptionTypes = ['in_store_pickup', 'inter_store']
          this.$set(this.loadedSettings, 'pickupCodeActivate', false)
          this.$set(this.loadedSettings, 'pickupCodeDisplayAllowedRoles', [])
          this.$set(this.loadedSettings, 'pickupCodeResendAllowedRoles', [])
        }
        if (res.pick_pack_ship) {
          this.loadedSettings.activatePickPackShip = res.pick_pack_ship.activate
        } else {
          this.loadedSettings.activatePickPackShip = false
        }
        if (res.preparation) {
          this.$set(this.loadedSettings, 'boxForPreparation', res.preparation.box_selection?.activate || false)
          this.$set(this.loadedSettings, 'boxForPreparationTypes', res.preparation.box_selection?.order_types || [])
          this.loadedSettings.preparation = {
            boxTypes: res.preparation.box_types.map((box) => convertBoxFromApiFormat(box, this.$i18n.locale)),
          }
        } else {
          this.$set(this.loadedSettings, 'boxForPreparation', false)
          this.$set(this.loadedSettings, 'boxForPreparationTypes', [])
          this.loadedSettings.preparation = { boxTypes: [] }
        }

        if (res.payments) {
          this.$set(this.loadedSettings, 'payments', {
            payment_methods: res.payments.payment_methods || [],
          })
        } else {
          this.$set(this.loadedSettings, 'payments', { payment_methods: [] })
        }
      },
      validateOrdersVisibility() {
        this.loadedSettings.ordersVisibilityErrorKey = ''
        // The block is only visible to and editable by Super Admins; a non-admin merely
        // round-trips the stored (already back-end-validated) value. Skip validation for
        // them — otherwise an invalid block would block every settings save for a role
        // that can neither see nor fix it.
        if (!this.isOctipasAdmin) {
          return true
        }
        // For a Super Admin, conditions must be present and valid. A missing array means the
        // form never loaded (e.g. the default-settings fetch failed) — treat it as an error and
        // block the save instead of passing through with an empty config.
        const errorKey = validateOrdersVisibilityConditions(this.loadedSettings.ordersVisibilityConditions)
        if (!errorKey) {
          return true
        }
        EventBus.$emit('displaySnackbar', { display: true, text: this.$t(errorKey), color: 'error' })
        this.loadedSettings.ordersVisibilityErrorKey = errorKey
        this.$nextTick(() => {
          // The block lives in the "Display settings" tab; switch to it before scrolling
          // so the error is visible even if another tab is active.
          this.activeTab = this.displaySettingsTabIndex
          this.$nextTick(() => {
            document.getElementById('orders-visibility-block')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          })
        })
        return false
      },
      async validate() {
        const success = await this.$refs.form.validate()
        if (success) {
          if (!this.validateOrdersVisibility()) {
            return
          }
          if (!this.loadedSettings.pickupDeadlineAdjustmentHour) {
            this.loadedSettings.pickupDeadlineAdjustmentHour = '23:59'
          }
          if (!this.loadedSettings.clickAndReservePickupDeadlineAdjustmentHour) {
            this.loadedSettings.clickAndReservePickupDeadlineAdjustmentHour = '23:59'
          }
          let body = {
            country_code: this.selectedCountry,
            delays: {
              global: {
                maximum_pickup_time: this.loadedSettings.globalClientDelay * 86400,
                pickup_timeout: {
                  activate: this.loadedSettings.activatePickupTimeout,
                  activate_pickup_deadline_adjustment:
                    this.loadedSettings.activatePickupTimeout && this.loadedSettings.activatePickupDeadlineAdjustment,
                  pickup_deadline_adjustment_hour: this.loadedSettings.pickupDeadlineAdjustmentHour,
                  activate_pickup_deadline_extension: this.loadedSettings.activatePickupDeadlineExtension,
                  pickup_deadline_extension_limit: this.loadedSettings.pickupDeadlineExtensionLimit * 86400,
                },
              },
              click_and_collect: {
                maximum_response_time: this.loadedSettings.clickAndCollectSellerDelay * 3600,
                preparation_slots_search_period:
                  this.loadedSettings.clickAndCollectPreparationSlotsSearchPeriod * 86400,
              },
              click_and_reserve: {
                maximum_response_time: this.loadedSettings.eReservationSellerDelay * 3600,
                maximum_pickup_time: this.loadedSettings.eReservationClientDelay * 86400,
                preparation_slots_search_period: this.loadedSettings.eReservationPreparationSlotsSearchPeriod * 86400,
                pickup_timeout: {
                  activate: this.loadedSettings.activateClickAndReservePickupTimeout,
                  activate_pickup_deadline_adjustment:
                    this.loadedSettings.activateClickAndReservePickupTimeout &&
                    this.loadedSettings.activateClickAndReservePickupDeadlineAdjustment,
                  pickup_deadline_adjustment_hour: this.loadedSettings.clickAndReservePickupDeadlineAdjustmentHour,
                  activate_pickup_deadline_extension:
                    this.loadedSettings.activateClickAndReservePickupDeadlineExtension,
                  pickup_deadline_extension_limit:
                    this.loadedSettings.clickAndReservePickupDeadlineExtensionLimit * 86400,
                },
              },
              ship_from_store: {
                maximum_response_time: this.loadedSettings.shipFromStoreSellerDelay * 3600,
                preparation_slots_search_period: this.loadedSettings.shipFromStorePreparationSlotsSearchPeriod * 86400,
              },
            },
            delivery_slip: {
              logo_header_url: this.loadedSettings.logo_header_url,
              footer_text: this.loadedSettings.footer_text,
              display_fields: {
                email: this.loadedSettings.display_fields.email,
                phone: this.loadedSettings.display_fields.phone,
                barcode: this.loadedSettings.display_fields.barcode,
              },
            },
            permissions: {
              pickup_order_cancel_roles: this.loadedSettings.pickupOrderCancelRoles || [],
              pickup_ebooking_cancel_roles: this.loadedSettings.ebookingCancelRoles || [],
            },
            display: {
              display_parcel_delivery_button: this.loadedSettings.displayParcelDeliveryButton,
              display_ebooking_delivery_button: this.loadedSettings.displayEbookingDeliveryButton,
              display_deliver_package_to_transporter_button:
                this.loadedSettings.displayDeliverPackageToTransporterButton,
              display_fast_preparation_button: this.loadedSettings.displayFastPreparationButton,
              display_stock_indicator_on_product: this.loadedSettings.displayStockIndicatorOnProduct,
              display_custom_cancel_reason: this.loadedSettings.displayCustomCancelReason,
              display_products_custom_cancel_reason: this.loadedSettings.displayProductsCustomCancelReason,
              display_order_to_put_back_in_stock: this.loadedSettings.displayOrderToPutBackInStock,
              display_address_edit_buttons: this.loadedSettings.displayAddressEditButtons,
              display_orders_global_search: this.loadedSettings.displayOrdersGlobalSearch,
              item_cancel_reasons: this.loadedSettings.itemCancelReasons,
              order_cancel_reasons: this.loadedSettings.orderCancelReasons,
              customer_information: {
                customer_name: this.loadedSettings.customerInformation.customerName,
                customer_email: this.loadedSettings.customerInformation.customerEmail,
                addresses: this.loadedSettings.customerInformation.addresses,
                full_phone_number: this.loadedSettings.customerInformation.fullPhoneNumber,
                last_four_digit_phone_number: this.loadedSettings.customerInformation.lastFourDigitPhoneNumber,
              },
              display_customer_edit_buttons: this.loadedSettings.displayCustomerEditButtons,
              display_force_delivered_button: this.loadedSettings.displayForceDelivered,
              display_retry_blocked_orders_button: this.loadedSettings.displayRetryBlockedOrdersButton,
              display_pickup_deadline_extension_button: this.loadedSettings.displayPickupDeadlineExtensionButton,
              display_ebooking_pickup_deadline_extension_button:
                this.loadedSettings.displayEbookingPickupDeadlineExtensionButton,
              display_waiting_payment_orders_menu: this.loadedSettings.displayWaitingPaymentOrdersMenu,
              orders_visibility: {
                conditions: this.loadedSettings.ordersVisibilityConditions || [],
              },
            },
            orchestration: {
              max_daily_allocations: {
                by_default: {
                  activate: this.loadedSettings.maxDailyAllocationsByDefaultActivate,
                  value: this.loadedSettings.maxDailyAllocationsByDefaultValue,
                },
                per_locations: this.loadedSettings.maxDailyAllocationsPerLocations,
              },
            },
            notifications: {
              on_create_sub_order: {
                email: {
                  activate: this.loadedSettings.onCreateSubOrderEmail.activate,
                  location: {
                    activate: this.loadedSettings.onCreateSubOrderEmail.location.activate,
                  },
                  location_managers: {
                    activate: this.loadedSettings.onCreateSubOrderEmail.location_managers.activate,
                    restrict_to_main: this.loadedSettings.onCreateSubOrderEmail.location_managers.restrict_to_main,
                  },
                },
                push_notification: {
                  activate: this.loadedSettings.onCreateSubOrderPushNotificationActivate,
                },
              },
              alerts: this.loadedSettings.alerts?.map((alert) => {
                return {
                  name: alert.name,
                  activate: alert.activate || false,
                  type: alert.type || 'email',
                  on_status: alert.onStatus,
                  emails: alert.emails,
                  template_id: alert.templateId,
                }
              }),
            },
            delivery: {
              locker: {
                for_preparation: {
                  activate: this.loadedSettings.lockerForPreparation,
                  order_types: this.loadedSettings.lockerForPreparationTypes,
                },
                for_reception: {
                  activate: this.loadedSettings.lockerForReception,
                  order_types: this.loadedSettings.lockerForReceptionTypes,
                },
              },
              pickup_code: {
                activate: this.loadedSettings.pickupCodeActivate,
                display_allowed_roles: this.loadedSettings.pickupCodeDisplayAllowedRoles,
                resend_allowed_roles: this.loadedSettings.pickupCodeResendAllowedRoles,
              },
            },
            pick_pack_ship: {
              activate: this.loadedSettings.activatePickPackShip,
            },
            preparation: {
              box_selection: {
                activate: this.loadedSettings.boxForPreparation || false,
                order_types: this.loadedSettings.boxForPreparationTypes || [],
              },
              box_types: this.loadedSettings.preparation.boxTypes.map(convertBoxToApiFormat),
            },
            payments: {
              payment_methods: this.loadedSettings.payments?.payment_methods || [],
            },
          }

          let myInit = {
            headers: {
              Authorization: 'Bearer ' + this.$auth.accessToken,
            },
            body: body,
          }
          if (this.areNewSettings) {
            this.createSettings(myInit)
          } else {
            // if the country code exists in db, it will update values
            this.updateSettings(myInit)
          }
        } else {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('settings.invalidSettings'),
            color: 'error',
          })
        }
      },
      createSettings(myInit) {
        // create settings for a country
        this.loading = true
        this.$Amplify.API.post('settings', '/v1/settings', myInit)
          .then((res) => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('settings.settingsCreated'),
              color: 'success',
            })
            this.loadedSettings.id = res._id
            this.areNewSettings = false
            this.loading = false
          })
          .catch((e) => {
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: e.message,
              color: 'error',
            })
            this.loading = false
          })
      },
      updateSettings(myInit) {
        // update settings with new values
        this.loading = true
        this.$Amplify.API.put('settings', '/v1/settings/' + this.$route.params.id, myInit)
          .then(() => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('settings.settingsUpdated'),
              color: 'success',
            })
            this.loading = false
          })
          .catch((e) => {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: e.message,
              color: 'error',
            })
            this.loading = false
          })
      },
    },
  }
</script>
